import http from "./httpService";

/**
 * @typedef {import('../types/api.js').BaseApiResponse} BaseApiResponse
 */

/**
 * @typedef {Object} TrainerAcademyModule
 * @property {number} id - Module ID
 * @property {string} title - Module title
 * @property {string|null} blurb - Module blurb/description
 * @property {string} status - Module status (e.g., "published")
 * @property {string|null} thumbnail - Thumbnail URL
 * @property {string|null} file_url - File URL
 * @property {string|null} scorm_uuid - SCORM UUID
 * @property {string|null} scorm_origin - SCORM origin file identifier
 * @property {number} created_by - User ID who created the module
 * @property {number|null} order - Display order
 * @property {string|null} filename - Filename
 * @property {string} created_at - Creation timestamp
 * @property {string} updated_at - Last update timestamp
 * @property {number} completed - Number of completions
 */

/**
 * @typedef {Object} AdminModulesListData
 * @property {number} page - Current page number
 * @property {number} limit - Items per page
 * @property {number} total_page - Total number of pages
 * @property {number} total_rows - Total number of rows
 * @property {TrainerAcademyModule[]} rows - Array of trainer academy modules
 */

/**
 * @typedef {Object} AdminAddExistingScormRequest
 * @property {string} uuid - SCORM UUID
 * @property {string} origin_file - SCORM origin file identifier
 * @property {string} status - Module status (e.g., "published")
 * @property {string} title - Module title
 * @property {number} order - Display order
 */

/**
 * @typedef {Object} TrainerAcademyModuleData
 * @property {number} id - Module ID
 * @property {string} title - Module title
 * @property {string|null} blurb - Module blurb/description
 * @property {string|null} thumbnail - Thumbnail URL
 * @property {string|null} filename - Filename
 * @property {string|null} scorm_uuid - SCORM UUID
 * @property {string|null} scorm_origin - SCORM origin file identifier
 * @property {string} status - Module status
 * @property {number} created_by - User ID who created the module
 * @property {string} created_at - Creation timestamp
 * @property {string} updated_at - Last update timestamp
 * @property {number} order - Display order
 */

/**
 * @typedef {Object} AdminAddModuleFormData
 * @property {string} title - Module title
 * @property {string} status - Module status ("published", "draft")
 * @property {File} file - Module file
 * @property {number} order - Display order
 */

/**
 * @typedef {Object} AdminTrainerProgressParams
 * @property {string} [search] - Search query
 * @property {number} [limit] - Items per page
 * @property {number} [page] - Page number
 * @property {"user_id"|"email"|"fullname"|"date_added"} [sort_by] - Field to sort by
 * @property {"asc"|"desc"} [sort_dir] - Sort direction
 */

/**
 * @typedef {Object} TrainerProgressRow
 * @property {number} user_id - User ID
 * @property {string} fullname - User full name
 * @property {string} email - User email
 * @property {string} date_added - Date added timestamp
 * @property {number} progress - Progress percentage
 * @property {string} status - Training status (e.g., "In Training")
 */

/**
 * @typedef {Object} AdminTrainerProgressData
 * @property {number} page - Current page number
 * @property {number} limit - Items per page
 * @property {number} total_page - Total number of pages
 * @property {number} total_rows - Total number of rows
 * @property {TrainerProgressRow[]} rows - Array of trainer progress records
 */

/**
 * @typedef {Object} AcademyProgress
 * @property {number} id - Progress ID
 * @property {string} uuid - Progress UUID
 * @property {number} trainer_id - Trainer ID
 * @property {string} created_at - Creation timestamp
 * @property {string} updated_at - Last update timestamp
 * @property {string} trainer_name - Trainer name
 * @property {string} trainer_email - Trainer email
 * @property {string} trainer_academy_title - Trainer academy title
 * @property {number} progress - Progress percentage
 * @property {string|null} survey_status - Survey status
 * @property {string} engagement_time - Engagement time string
 * @property {string} title - Module title
 */

/**
 * @typedef {Object} TrainerProgressDetailData
 * @property {number} user_id - User ID
 * @property {string} fullname - User full name
 * @property {string} email - User email
 * @property {string} date_added - Date added timestamp
 * @property {number} progress - Overall progress percentage
 * @property {string} status - Training status (e.g., "In Training", "Certified")
 * @property {AcademyProgress[]} academy_progresses - Array of academy progress records
 */

class TrainerAcademyService {
  /**
   * Get trainer academies with pagination and filters
   * @param {Object} params - Query parameters
   * @param {string} params.search - Search query
   * @param {number} params.page - Page number
   * @param {number} params.limit - Items per page
   * @param {string} params.sort_by - Sort field
   * @param {string} params.sort_dir - Sort direction (asc/desc)
   * @returns {Promise<BaseApiResponse<TrainerAcademyListResponse>>}
   */
  async getTrainerAcademies(params = {}) {
    return await http.get("/trainer-academy", { params });
  }

  /**
   * Get current user's trainer academy progress
   * @returns {Promise<BaseApiResponse<UserProgressResponse>>}
   */
  async getMyProgress() {
    return await http.get("/trainer-academy/my-progress");
  }

  /**
   * Get trainer academy view URL
   * @param {number} id - Trainer academy ID
   * @returns {Promise<BaseApiResponse<TrainerAcademyViewResponse>>}
   */
  async getTrainerAcademyView(id) {
    return await http.get(`/trainer-academy/view/${id}`);
  }

  async getCertificate() {
    return await http.get(`/trainer-academy/certificate`, {
      responseType: "blob",
    });
  }

  /**
   * Get all trainer academy modules (admin)
   * @returns {Promise<BaseApiResponse & { data: AdminModulesListData }>}
   */
  async adminGetModules() {
    return await http.get("/admin/trainer-academy");
  }

  /**
   * Add a new trainer academy module (admin)
   * @param {FormData|AdminAddModuleFormData} data - Form data containing title, status, file, and order
   * @returns {Promise<BaseApiResponse & { data: TrainerAcademyModuleData }>}
   */
  async adminAddModule(data) {
    const config =
      data instanceof FormData
        ? { headers: { "Content-Type": "multipart/form-data" } }
        : {};
    return await http.post("/admin/trainer-academy", data, config);
  }

  /**
   * Get trainer progress with pagination and filters (admin)
   * @param {AdminTrainerProgressParams} [params={}] - Query parameters for filtering, pagination, and sorting
   * @returns {Promise<BaseApiResponse & { data: AdminTrainerProgressData }>}
   */
  async adminGetTrainerProgress(params = {}) {
    return await http.get("/admin/trainer-academy/progress", { params });
  }

  /**
   * Get trainer progress details by ID (admin)
   * @param {number} id - Trainer user ID
   * @returns {Promise<BaseApiResponse & { data: TrainerProgressDetailData }>}
   */
  async adminGetTrainerProgressById(id) {
    return await http.get(`/admin/trainer-academy/progress/${id}`);
  }

  /**
   * Add an existing SCORM module to trainer academy (admin)
   * @param {AdminAddExistingScormRequest} data - SCORM module data
   * @returns {Promise<BaseApiResponse & { data: TrainerAcademyModuleData }>}
   */
  async adminAddExistingScorm(data) {
    return await http.post("/admin/trainer-academy/scorm", data);
  }

  /**
   * Update a trainer academy module (admin)
   * @param {number} id - Module ID
   * @param {FormData|Partial<AdminAddModuleFormData>} data - Updated module data
   * @returns {Promise<BaseApiResponse & { data: TrainerAcademyModuleData }>}
   */
  async adminUpdateModule(id, data) {
    const config =
      data instanceof FormData
        ? { headers: { "Content-Type": "multipart/form-data" } }
        : {};
    return await http.put(`/admin/trainer-academy/${id}`, data, config);
  }

  /**
   * Delete a trainer academy module (admin)
   * @param {number} id - Module ID
   * @returns {Promise<BaseApiResponse>}
   */
  async adminDeleteModule(id) {
    return await http.delete(`/admin/trainer-academy/${id}`);
  }

  async adminDownloadTrainerCertificate(trainerId) {
    return await http.get(`/admin/trainer-academy/certificate/${trainerId}`, {
      responseType: "blob",
    });
  }
}

export default new TrainerAcademyService();
